import React, { useMemo, useState } from "react";
import { Button, Form, Input, Modal, Radio } from "antd";
import { CloseOutlined, ExclamationCircleFilled, LockOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { isMobile } from "react-device-detect";
import {
  CREATOR_DECLINE_REASON_OPTIONS,
} from "helpers/creator-decline-helper";
import { reviewBusinessApplication } from "services/SauceService";
import "./DeclineCreatorModal.scss";
const OTHER_REASON_CATEGORY = "Other";

type DeclineCreatorFormValues = {
  reasonCategory?: string;
  reasonText?: string;
};

interface DeclineCreatorModalProps {
  declineInfluencer: any | null;
  onCancel: () => void;
  onSave?: () => Promise<void> | void;
}

const DeclineCreatorModal: React.FC<DeclineCreatorModalProps> = ({
  declineInfluencer,
  onCancel,
  onSave,
}) => {
  const open = declineInfluencer != null;
  const [form] = Form.useForm<DeclineCreatorFormValues>();
  const [loading, setLoading] = useState(false);

  const reasonCategory = Form.useWatch("reasonCategory", form);
  const reasonText = Form.useWatch("reasonText", form);
  const isOtherReasonWithoutText = reasonCategory === "other" && !String(reasonText || "").trim();
  const isSubmitDisabled = !reasonCategory || isOtherReasonWithoutText;

  const creatorName = declineInfluencer?.name;
  const creatorAvatarUrl = declineInfluencer?.avatarUrl || undefined;
  const creatorCategories = declineInfluencer?.categories || [];
  const creatorFollowersCount = Math.max(
    declineInfluencer?.instagramFollowersCount || 0,
    declineInfluencer?.tiktokFollowersCount || 0,
  );

  const title = useMemo(() => {
    if (!creatorName) return "Decline this creator?";
    return `Decline ${creatorName}?`;
  }, [creatorName]);
  const creatorInitial = (creatorName || "C").trim().charAt(0).toUpperCase();

  const followersLabel = useMemo(() => {
    const count = Number(creatorFollowersCount || 0);
    if (!count) return "0 followers";
    if (count >= 1000000) return `${(count / 1000000).toFixed(1).replace(".0", "")}M followers`;
    if (count >= 1000) return `${(count / 1000).toFixed(1).replace(".0", "")}K followers`;
    return `${count} followers`;
  }, [creatorFollowersCount]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      await reviewBusinessApplication({
        bookingId: declineInfluencer?.bookingId,
        collabId: declineInfluencer?.collabId,
        status: "reject",
        rejectReasonCategory: values.reasonCategory,
        rejectReasonText: String(values.reasonText || "").trim(),
      });
      form.resetFields();
      await onSave?.();
      onCancel();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={null}
      onCancel={handleCancel}
      footer={null}
      centered
      width={850}
      closeIcon={<CloseOutlined />}
      className={`decline-creator-modal ${isMobile ? "mobile" : ""}`}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} className="decline-creator-modal__form">
        <div className="decline-creator-modal__header">
          <h2>
            <ExclamationCircleFilled className={`decline-creator-modal__title-icon ${isMobile ? "" : "hidden"}`} />
            <span className={isMobile ? "hidden" : ""}>{title}</span>
            <span className={isMobile ? "" : "hidden"}>Decline this creator?</span>
          </h2>
          <p>{isMobile ? "This will remove them from your campaign." : "You're about to remove this creator from your campaign."}</p>
        </div>

        <div className="decline-creator-modal__body">
          <div className="decline-creator-modal__main">
            <div className={`decline-creator-modal__private-card decline-creator-modal__private-card--mobile ${isMobile ? "" : "hidden"}`}>
              <div className="decline-creator-modal__private-icon">
                <LockOutlined />
              </div>
              <div>
                <p>Your feedback is private and helps us make better recommendations.</p>
              </div>
            </div>

            <div className="decline-creator-modal__section-title">
              <h3>Why are you declining?</h3>
              <p className={isMobile ? "hidden" : ""}>Select the main reason.</p>
            </div>

            <Form.Item
              name="reasonCategory"
              rules={[{ required: true, message: "Please select a reason." }]}
              className="decline-creator-modal__reason-form-item"
            >
              <Radio.Group className="decline-creator-modal__reasons-grid">
                {CREATOR_DECLINE_REASON_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value} className={`decline-creator-modal__reason-card ${isMobile ? "mobile" : ""}`}>
                    <span className="decline-creator-modal__reason-radio">
                      <span className="decline-creator-modal__reason-dot" />
                    </span>
                    <span>{option.label}</span>
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>

            {reasonCategory === OTHER_REASON_CATEGORY && (
              <Form.Item
                label="Tell us briefly"
                name="reasonText"
                rules={[
                  { required: true, message: "Please tell us briefly." }
                ]}
                className="decline-creator-modal__other-text"
              >
                <Input.TextArea rows={2} maxLength={200} placeholder="Tell us briefly" className="decline-creator-modal__other-text-textarea" />
              </Form.Item>
            )}
          </div>

          <aside className={`decline-creator-modal__side ${isMobile ? "hidden" : ""}`}>
            <div className="decline-creator-modal__creator-card">
              {creatorAvatarUrl ? (
                <img
                  src={creatorAvatarUrl}
                  alt={creatorName || "Creator"}
                  className="decline-creator-modal__avatar-img"
                />
              ) : (
                <div className="decline-creator-modal__avatar">{creatorInitial}</div>
              )}
              <div>
                <h4>{creatorName || "Creator"}</h4>
                <p>{creatorCategories?.slice(0, 2).join(" • ") || "Creator"}</p>
                <span>
                  <UsergroupAddOutlined />
                  {followersLabel}
                </span>
              </div>
            </div>

            <div className="decline-creator-modal__private-card">
              <div className="decline-creator-modal__private-icon">
                <LockOutlined />
              </div>
              <div>
                <h5>Private feedback</h5>
                <p>Your feedback is private and helps us make better recommendations.</p>
              </div>
            </div>
          </aside>
        </div>

        <div className={`decline-creator-modal__footer ${isMobile ? "mobile" : ""}`}>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading} disabled={isSubmitDisabled}>
            Decline Creator
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default DeclineCreatorModal;
